<template>
  <section class="safe-step">
    <div class="safe-card">
      <p class="safe-kicker">Paso 3 de 4</p>
      <h2>Protección de compra</h2>
      <p class="safe-copy">Protégete por {{ perTicketLabel }} por ticket. Si un imprevisto personal validable te impide asistir o el evento se cancela, FOMO te respalda con reembolso.</p>

      <div class="safe-highlights">
        <span>Reembolso por imprevistos</span>
        <span>Respaldo por cancelación</span>
        <span>Sin cambiar tus accesos</span>
      </div>

      <div class="safe-toggle">
        <label class="safe-switch" :class="{ active: enabled }">
          <input v-model="enabled" type="checkbox">
          <span class="safe-switch__track"><span class="safe-switch__thumb"></span></span>
          <span class="safe-switch__copy">
            <strong>{{ enabled ? 'Protege tu compra ahora' : 'Continuar sin protección' }}</strong>
            <small>{{ enabled ? perTicketLabel + ' por ticket. FOMO te protege ante imprevistos personales validables o cancelación oficial del evento.' : 'Continúa normal y paga solo tus accesos.' }}</small>
          </span>
        </label>
        <button type="button" class="safe-skip" @click="enabled = false">Continuar sin protección</button>
      </div>
      <div class="safe-summary">
        <span>Costo de protección <strong>{{ formatMoney(enabled ? protectionTotal : 0) }}</strong></span>
        <span>Reembolso respaldado <strong>{{ formatMoney(enabled ? protectedTotal : 0) }}</strong></span>
      </div>
      <p class="safe-legal">FOMO te protege frente a imprevistos personales validables y cancelación oficial del evento. No cubre reprogramaciones, cambios de horario o venue, errores de compra, fees aceptados ni el valor del addon.</p>
      <button type="button" class="safe-cta" @click="submitChoice">{{ enabled ? 'Sí, proteger mi compra' : 'Continuar al pago' }}</button>
    </div>
  </section>
</template>

<script>
import { formatCurrencyAmount, normalizeCurrencyCode } from '../../common/currency.js'

const DEFAULT_FOMO_SAFE_PRICING = {
  enabled: true,
  currency: 'USD',
  tiers: [
    { max_amount: '30.00', unit_amount: '1.99' },
    { max_amount: '60.00', unit_amount: '2.99' },
    { max_amount: '100.00', unit_amount: '4.99' },
    { max_amount: null, unit_amount: '7.99' },
  ],
}

export default {
  name: "FomoSafeStep",
  props: {
    ticketsTable: { type: Array, default: () => [] },
    subtotal: { type: [Number, String], default: 0 },
    fee: { type: [Number, String], default: 0 },
    total: { type: [Number, String], default: 0 },
    presentmentCurrency: { type: String, default: 'USD' },
    fomoSafePricing: { type: Object, default: null },
  },
  emits: ["messageFromAddons"],
  data() { return { enabled: true } },
  computed: {
    resolvedPricing() {
      const source = this.fomoSafePricing && typeof this.fomoSafePricing === 'object' ? this.fomoSafePricing : {}
      const sourceTiers = Array.isArray(source.tiers) && source.tiers.length ? source.tiers : DEFAULT_FOMO_SAFE_PRICING.tiers
      return {
        enabled: source.enabled !== false,
        currency: normalizeCurrencyCode(source.currency || this.presentmentCurrency || DEFAULT_FOMO_SAFE_PRICING.currency, DEFAULT_FOMO_SAFE_PRICING.currency),
        tiers: sourceTiers.map((tier) => ({
          max_amount: tier && tier.max_amount != null ? this.toNumber(tier.max_amount) : null,
          unit_amount: this.toNumber(tier && tier.unit_amount),
        })),
      }
    },
    pricingCurrency() { return this.resolvedPricing.currency },
    protectedTotal() { return this.toNumber(this.subtotal) },
    eligibleTickets() { return Array.isArray(this.ticketsTable) ? this.ticketsTable.filter((ticket) => this.toNumber(ticket.amount) > 0) : [] },
    eligibleCount() { return this.eligibleTickets.length },
    protectionPrices() { return this.eligibleTickets.map((ticket) => this.resolveProtectionPrice(this.toNumber(ticket.amount))) },
    minProtectionPrice() { return this.protectionPrices.length ? Math.min(...this.protectionPrices) : 0 },
    maxProtectionPrice() { return this.protectionPrices.length ? Math.max(...this.protectionPrices) : 0 },
    perTicketLabel() {
      if (!this.eligibleCount) return this.formatMoney(0)
      if (this.minProtectionPrice === this.maxProtectionPrice) return this.formatMoney(this.minProtectionPrice)
      return 'desde ' + this.formatMoney(this.minProtectionPrice)
    },
    addons() {
      return this.eligibleTickets.map((ticket, index) => {
        const price = this.resolveProtectionPrice(this.toNumber(ticket.amount))
        const holder = [ticket.first_name, ticket.last_name].filter(Boolean).join(" ").trim()
        return { code: "fomo_safe", name: "Fomo Safe", description: "Protección para imprevistos personales validables o cancelación oficial del evento.", quantity: 1, unit_amount: price.toFixed(2), total_amount: price.toFixed(2), reference_name: ticket.name || "Acceso", holder_name: holder || "Ticket " + (index + 1) }
      })
    },
    protectionTotal() { return this.addons.reduce((sum, addon) => sum + this.toNumber(addon.total_amount), 0) },
  },
  methods: {
    toNumber(value) { const parsed = Number.parseFloat(value); return Number.isNaN(parsed) ? 0 : parsed },
    formatMoney(value) { return formatCurrencyAmount(value, this.pricingCurrency) },
    resolveProtectionPrice(amount) {
      const tiers = this.resolvedPricing.tiers
      for (let index = 0; index < tiers.length; index += 1) {
        const tier = tiers[index]
        if (tier.max_amount === null || amount <= tier.max_amount) {
          return tier.unit_amount
        }
      }
      return 0
    },
    submitChoice() { this.$emit("messageFromAddons", this.enabled ? this.addons : [], this.enabled ? this.protectionTotal : 0) },
  },
}
</script>

<style scoped>
.safe-step {
  color: #edf2fb;
}

.safe-card {
  position: relative;
  max-width: 860px;
  margin: 0 auto;
  padding: 24px;
  border: 1px solid rgba(197, 207, 224, 0.18);
  border-radius: 30px;
  background:
    radial-gradient(circle at top left, rgba(217, 226, 242, 0.1), transparent 18%),
    radial-gradient(circle at 82% 14%, rgba(127, 101, 160, 0.18), transparent 28%),
    linear-gradient(160deg, rgba(9, 10, 14, 0.94) 0%, rgba(12, 13, 18, 0.92) 58%, rgba(10, 11, 16, 0.96) 100%);
  box-shadow: 0 26px 78px rgba(0, 0, 0, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(28px) saturate(145%);
  overflow: hidden;
}

.safe-card::before,
.safe-card::after {
  content: '';
  position: absolute;
  pointer-events: none;
}

.safe-card::before {
  inset: 1px;
  border-radius: inherit;
  background:
    linear-gradient(130deg, rgba(255, 255, 255, 0.16), transparent 18%, transparent 68%, rgba(168, 181, 206, 0.08)),
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.09), transparent 24%);
  opacity: 0.62;
}

.safe-card::after {
  inset: -28% auto auto -24%;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(192, 203, 224, 0.16), transparent 70%);
  filter: blur(20px);
  opacity: 0.8;
}

.safe-kicker {
  position: relative;
  z-index: 1;
  margin: 0 0 8px;
  color: #ced8ea;
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.safe-card h2 {
  position: relative;
  z-index: 1;
  margin: 0 0 10px;
  color: #f4f7ff;
  font-size: 34px;
  line-height: 1.02;
}

.safe-copy {
  position: relative;
  z-index: 1;
  max-width: 58ch;
  margin: 0 0 18px;
  color: rgba(206, 215, 230, 0.84);
  line-height: 1.7;
}

.safe-highlights {
  position: relative;
  z-index: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}

.safe-highlights span {
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(197, 207, 224, 0.12);
  color: #dbe4f6;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.safe-toggle {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 12px;
}

.safe-switch {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 18px;
  align-items: center;
  padding: 22px;
  border: 1px solid rgba(197, 207, 224, 0.14);
  border-radius: 24px;
  background:
    radial-gradient(circle at 14% 0%, rgba(221, 229, 247, 0.08), transparent 26%),
    radial-gradient(circle at 84% 18%, rgba(146, 119, 200, 0.1), transparent 28%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.02));
  cursor: pointer;
  transition: transform 0.34s cubic-bezier(0.34, 1.2, 0.64, 1), border-color 0.28s ease, box-shadow 0.28s ease;
  backdrop-filter: blur(22px);
  overflow: hidden;
}

.safe-switch::before {
  content: '';
  position: absolute;
  inset: -120% auto auto -12%;
  width: 42%;
  height: 280%;
  background: linear-gradient(115deg, transparent 12%, rgba(255, 255, 255, 0.34) 48%, transparent 78%);
  transform: rotate(16deg);
  animation: safe-sheen 7.2s ease-in-out infinite;
  opacity: 0.22;
  pointer-events: none;
}

.safe-switch.active {
  border-color: rgba(182, 196, 226, 0.24);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(160, 173, 202, 0.08);
  transform: translateY(-1px);
}

.safe-switch input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.safe-switch__track {
  position: relative;
  width: 98px;
  height: 58px;
  padding: 2px;
  border-radius: 999px;
  overflow: hidden;
  isolation: isolate;
  background: rgba(11, 13, 19, 0.94);
  border: 1px solid rgba(197, 207, 224, 0.12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 16px 28px rgba(0, 0, 0, 0.24);
}

.safe-switch__track::before {
  content: '';
  position: absolute;
  inset: -58%;
  z-index: 0;
  background: conic-gradient(from 0deg, rgba(33, 38, 56, 0) 0%, rgba(89, 107, 145, 0.18) 16%, rgba(214, 223, 244, 0.68) 30%, rgba(255, 255, 255, 0.92) 33%, rgba(129, 101, 184, 0.52) 37%, rgba(198, 208, 229, 0.28) 48%, rgba(33, 38, 56, 0) 58%, rgba(89, 107, 145, 0.18) 76%, rgba(214, 223, 244, 0.68) 90%, rgba(255, 255, 255, 0.92) 93%, rgba(129, 101, 184, 0.52) 97%, rgba(33, 38, 56, 0) 100%);
  animation: safe-ring-spin 5.2s linear infinite;
  opacity: 0.54;
}

.safe-switch__track::after {
  content: '';
  position: absolute;
  inset: 2px;
  z-index: 1;
  border-radius: inherit;
  background:
    radial-gradient(circle at 20% 20%, rgba(221, 229, 247, 0.14), transparent 34%),
    radial-gradient(circle at 82% 24%, rgba(146, 119, 200, 0.18), transparent 32%),
    linear-gradient(180deg, rgba(14, 16, 23, 0.98), rgba(8, 9, 14, 0.98));
}

.safe-switch.active .safe-switch__track {
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 18px 34px rgba(0, 0, 0, 0.28), 0 0 24px rgba(146, 119, 200, 0.16);
}

.safe-switch__thumb {
  position: absolute;
  top: 7px;
  left: 7px;
  z-index: 2;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background:
    radial-gradient(circle at 30% 28%, rgba(255, 255, 255, 0.92), rgba(225, 233, 248, 0.86) 38%, rgba(146, 119, 200, 0.92) 100%);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.28);
  transition: transform 0.42s cubic-bezier(0.34, 1.2, 0.64, 1), box-shadow 0.28s ease, filter 0.28s ease;
}

.safe-switch__thumb::before {
  content: '';
  position: absolute;
  inset: -8px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(173, 155, 221, 0.34), transparent 68%);
  filter: blur(10px);
  opacity: 0;
  transition: opacity 0.28s ease;
}

.safe-switch.active .safe-switch__thumb {
  transform: translateX(40px) scale(1.04);
  box-shadow: 0 16px 28px rgba(0, 0, 0, 0.32), 0 0 22px rgba(146, 119, 200, 0.24), inset 0 1px 0 rgba(255, 255, 255, 0.3);
  filter: saturate(108%);
  animation: safe-thumb-bounce 0.46s cubic-bezier(0.34, 1.2, 0.64, 1);
}

.safe-switch.active .safe-switch__thumb::before {
  opacity: 1;
}

.safe-switch__copy {
  display: grid;
  gap: 6px;
}

.safe-switch__copy strong {
  color: #f4f7ff;
  font-size: 28px;
  line-height: 1;
}

.safe-switch__copy small {
  color: rgba(190, 200, 218, 0.72);
  line-height: 1.6;
}

.safe-skip {
  width: fit-content;
  padding: 0;
  border: 0;
  background: transparent;
  color: #becbe3;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.safe-summary {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin: 18px 0 12px;
  padding: 16px 18px;
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.055), rgba(255, 255, 255, 0.018));
  border: 1px solid rgba(197, 207, 224, 0.12);
  color: #dce5f5;
  backdrop-filter: blur(18px);
}

.safe-summary span {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.safe-summary strong {
  color: #f4f7ff;
}

.safe-legal {
  position: relative;
  z-index: 1;
  margin: 0 0 16px;
  color: rgba(190, 200, 218, 0.68);
  font-size: 13px;
  line-height: 1.6;
}

.safe-cta {
  position: relative;
  z-index: 1;
  width: 100%;
  min-height: 62px;
  border: 1px solid rgba(197, 207, 224, 0.2);
  border-radius: 999px;
  background:
    radial-gradient(circle at 18% 0%, rgba(221, 229, 247, 0.22), transparent 34%),
    radial-gradient(circle at 82% 18%, rgba(146, 119, 200, 0.22), transparent 28%),
    linear-gradient(180deg, rgba(24, 26, 34, 0.96) 0%, rgba(11, 12, 18, 0.98) 100%);
  color: #eef3ff;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  box-shadow: 0 18px 34px rgba(0, 0, 0, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.09);
  overflow: hidden;
}

.safe-cta::before {
  content: '';
  position: absolute;
  inset: -140% auto auto -10%;
  width: 48%;
  height: 300%;
  background: linear-gradient(115deg, transparent 12%, rgba(255, 255, 255, 0.42) 48%, transparent 78%);
  transform: rotate(16deg);
  animation: safe-sheen 6.4s ease-in-out infinite;
}

.safe-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 24px 42px rgba(0, 0, 0, 0.34), 0 0 0 1px rgba(197, 207, 224, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

@keyframes safe-sheen {
  0%,
  100% {
    transform: translate3d(-140%, 0, 0) rotate(16deg);
    opacity: 0;
  }

  20% {
    opacity: 0.18;
  }

  48% {
    transform: translate3d(220%, 0, 0) rotate(16deg);
    opacity: 0.38;
  }

  60% {
    opacity: 0;
  }
}

@keyframes safe-ring-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes safe-thumb-bounce {
  0% {
    transform: translateX(0) scale(1);
  }

  68% {
    transform: translateX(44px) scale(1.08);
  }

  100% {
    transform: translateX(40px) scale(1.04);
  }
}

@media (max-width: 768px) {
  .safe-card {
    padding: 22px;
    border-radius: 22px;
  }

  .safe-card h2 {
    font-size: 28px;
  }

  .safe-summary {
    grid-template-columns: 1fr;
  }

  .safe-switch {
    grid-template-columns: 1fr;
    padding: 16px;
  }

  .safe-switch__track {
    width: 92px;
    height: 56px;
  }

  .safe-switch__thumb {
    width: 38px;
    height: 38px;
  }

  .safe-switch.active .safe-switch__thumb {
    transform: translateX(38px) scale(1.04);
  }
}
</style>
